import React, { useState } from 'react';
import { IdentifierType, ClickableIdentifier } from './ClickableIdentifier';
import { useTraceabilityResolver, TraceabilityResult } from '../../hooks/useTraceabilityResolver';
import { ShipmentEntry } from '../../types/shipment';
import { IngredientTraceCode } from '../../types/ingredientTraceCode';
import { BatchRecord } from '../../types/batchRecord';
import { BakingProductionLog } from '../../types/bakingProductionLog';
import { TraceCode } from '../../types/traceCode';
import { DeliveryLog } from '../../types/deliveryLog';

interface TraceabilityModalProps {
  type: IdentifierType;
  value: string;
  onClose: () => void;
  onNavigate: (type: IdentifierType, value: string) => void;
}

const TYPE_LABELS: Record<IdentifierType, string> = {
  lotNumber: 'Supplier Lot #',
  traceCode: 'Internal Trace Code',
  batchLotNumber: 'Batch Lot #'
};

interface CollapsibleSectionProps {
  title: string;
  count: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  defaultExpanded = false,
  children
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '10px 14px',
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none'
        }}
      >
        <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
          {title}
          <span
            style={{
              marginLeft: '8px',
              padding: '2px 8px',
              backgroundColor: count > 0 ? '#2196F3' : '#bdbdbd',
              color: 'white',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            {count}
          </span>
        </span>
        <span
          style={{
            fontSize: '14px',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}
        >
          &#9654;
        </span>
      </div>
      {isExpanded && (
        <div style={{ padding: '12px 14px', overflowX: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const compactThStyle: React.CSSProperties = {
  padding: '6px 10px',
  textAlign: 'left',
  borderBottom: '1px solid #ddd',
  fontWeight: '600',
  fontSize: '11px',
  textTransform: 'uppercase',
  color: '#555',
  whiteSpace: 'nowrap'
};

const compactTdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid #eee',
  fontSize: '13px',
  whiteSpace: 'nowrap'
};

const emptyStyle: React.CSSProperties = {
  color: '#999',
  fontStyle: 'italic',
  fontSize: '13px',
  padding: '8px 0'
};

export const TraceabilityModal: React.FC<TraceabilityModalProps> = ({
  type,
  value,
  onClose,
  onNavigate
}) => {
  const result = useTraceabilityResolver(type, value);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Traceability Chain</h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#666' }}>
              {TYPE_LABELS[type]}: <strong style={{ color: '#2196F3' }}>{value}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: 0,
              width: '32px',
              height: '32px',
              lineHeight: '32px',
              textAlign: 'center'
            }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ReceivingSection entries={result.receivingEntries} onNavigate={onNavigate} />
          <IngredientTraceCodesSection traceCodes={result.ingredientTraceCodes} onNavigate={onNavigate} />
          <BatchRecordsSection records={result.batchRecords} onNavigate={onNavigate} />
          <BakingLogsSection logs={result.bakingLogs} onNavigate={onNavigate} />
          <FinishedProductTraceCodesSection traceCodes={result.finishedProductTraceCodes} onNavigate={onNavigate} />
          <DeliveryLogsSection logs={result.deliveryLogs} onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
};

// --- Section components ---

const ReceivingSection: React.FC<{ entries: ShipmentEntry[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ entries, onNavigate }) => (
  <CollapsibleSection title="Ingredient Receiving" count={entries.length} defaultExpanded={entries.length > 0}>
    {entries.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Date</th>
            <th style={compactThStyle}>Supplier</th>
            <th style={compactThStyle}>Ingredient</th>
            <th style={compactThStyle}>Lot #</th>
            <th style={compactThStyle}>Qty</th>
            <th style={compactThStyle}>Condition</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id}>
              <td style={compactTdStyle}>{entry.receivingDate || '-'}</td>
              <td style={compactTdStyle}>{entry.supplierName || '-'}</td>
              <td style={compactTdStyle}>{entry.ingredient || '-'}</td>
              <td style={compactTdStyle}>
                {entry.lotNumber ? (
                  <ClickableIdentifier value={entry.lotNumber} type="lotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>
                {entry.quantity > 0 ? `${entry.quantity} ${entry.unit}` : '-'}
              </td>
              <td style={compactTdStyle}>{entry.conditionUponArrival || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

const IngredientTraceCodesSection: React.FC<{ traceCodes: IngredientTraceCode[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ traceCodes, onNavigate }) => (
  <CollapsibleSection title="Ingredient Trace Codes" count={traceCodes.length} defaultExpanded={traceCodes.length > 0}>
    {traceCodes.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Ingredient</th>
            <th style={compactThStyle}>Supplier Lot #</th>
            <th style={compactThStyle}>Internal Trace Code</th>
            <th style={compactThStyle}>Date Used</th>
            <th style={compactThStyle}>Finished Product</th>
          </tr>
        </thead>
        <tbody>
          {traceCodes.map(tc => (
            <tr key={tc.id}>
              <td style={compactTdStyle}>{tc.ingredientName || '-'}</td>
              <td style={compactTdStyle}>
                {tc.supplierLotNumber ? (
                  <ClickableIdentifier value={tc.supplierLotNumber} type="lotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>
                {tc.internalTraceCode ? (
                  <ClickableIdentifier value={tc.internalTraceCode} type="traceCode" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>{tc.dateUsed || '-'}</td>
              <td style={compactTdStyle}>{tc.finishedProduct || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

const BatchRecordsSection: React.FC<{ records: BatchRecord[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ records, onNavigate }) => (
  <CollapsibleSection title="Batch Records" count={records.length} defaultExpanded={records.length > 0}>
    {records.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Product</th>
            <th style={compactThStyle}>Batch/Lot #</th>
            <th style={compactThStyle}>Production Date</th>
            <th style={compactThStyle}>Prepared By</th>
          </tr>
        </thead>
        <tbody>
          {records.map(record => (
            <tr key={record.id}>
              <td style={compactTdStyle}>{record.productName || '-'}</td>
              <td style={compactTdStyle}>
                {record.batchLotNumber ? (
                  <ClickableIdentifier value={record.batchLotNumber} type="batchLotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>{record.dateOfProduction || '-'}</td>
              <td style={compactTdStyle}>{record.preparedBy || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

const BakingLogsSection: React.FC<{ logs: BakingProductionLog[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ logs, onNavigate }) => (
  <CollapsibleSection title="Baking / Cooling / Freezing" count={logs.length} defaultExpanded={logs.length > 0}>
    {logs.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Product</th>
            <th style={compactThStyle}>Lot #</th>
            <th style={compactThStyle}>Start Time</th>
            <th style={compactThStyle}>Finish Time</th>
            <th style={compactThStyle}>Oven Temp</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id}>
              <td style={compactTdStyle}>{log.fillingName || '-'}</td>
              <td style={compactTdStyle}>
                {log.lotNumber ? (
                  <ClickableIdentifier value={log.lotNumber} type="batchLotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>{log.startTime ? formatDateTime(log.startTime) : '-'}</td>
              <td style={compactTdStyle}>{log.finishTime ? formatDateTime(log.finishTime) : '-'}</td>
              <td style={compactTdStyle}>{log.ovenTemp ? `${log.ovenTemp}\u00B0F` : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

const FinishedProductTraceCodesSection: React.FC<{ traceCodes: TraceCode[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ traceCodes, onNavigate }) => (
  <CollapsibleSection title="Finished Product Trace Codes" count={traceCodes.length} defaultExpanded={traceCodes.length > 0}>
    {traceCodes.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Product</th>
            <th style={compactThStyle}>Batch #</th>
            <th style={compactThStyle}>Production Date</th>
            <th style={compactThStyle}>Qty Produced</th>
            <th style={compactThStyle}>Customer</th>
          </tr>
        </thead>
        <tbody>
          {traceCodes.map(tc => (
            <tr key={tc.id}>
              <td style={compactTdStyle}>{tc.fillingName || '-'}</td>
              <td style={compactTdStyle}>
                {tc.batchLotNumber ? (
                  <ClickableIdentifier value={tc.batchLotNumber} type="batchLotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>{tc.productionDate || '-'}</td>
              <td style={compactTdStyle}>{tc.quantityProduced != null ? `${tc.quantityProduced} dozens` : '-'}</td>
              <td style={compactTdStyle}>{tc.customerDestination || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

const DeliveryLogsSection: React.FC<{ logs: DeliveryLog[]; onNavigate: (type: IdentifierType, value: string) => void }> = ({ logs, onNavigate }) => (
  <CollapsibleSection title="Delivery" count={logs.length} defaultExpanded={logs.length > 0}>
    {logs.length === 0 ? (
      <p style={emptyStyle}>No records found</p>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={compactThStyle}>Date</th>
            <th style={compactThStyle}>Product</th>
            <th style={compactThStyle}>Batch #</th>
            <th style={compactThStyle}>Customer</th>
            <th style={compactThStyle}>Qty</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id}>
              <td style={compactTdStyle}>{log.deliveryDate || '-'}</td>
              <td style={compactTdStyle}>{log.fillingName || '-'}</td>
              <td style={compactTdStyle}>
                {log.batchLotNumber ? (
                  <ClickableIdentifier value={log.batchLotNumber} type="batchLotNumber" onClick={onNavigate} />
                ) : '-'}
              </td>
              <td style={compactTdStyle}>{log.customerDestination || '-'}</td>
              <td style={compactTdStyle}>{log.quantity != null ? log.quantity.toFixed(2) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </CollapsibleSection>
);

function formatDateTime(isoString: string): string {
  if (!isoString) return '';
  const [datePart, timePart] = isoString.split('T');
  if (!timePart) return datePart;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  const date = new Date(year, month - 1, day, hour, minute);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
